import { create } from 'zustand';
import { DailyHoroscopeData } from '@services/kundliService';

interface CachedHoroscope {
    data: DailyHoroscopeData;
    fetchedAt: number;
    language: string;
    date: 'yesterday' | 'today' | 'tomorrow';
}

interface InsightsState {
    horoscopes: Record<string, CachedHoroscope>; // key: `${date}_${language}`
    isLoading: boolean;
    error: string | null;
    isProfileMissing: boolean;

    // Actions
    setHoroscope: (date: 'yesterday' | 'today' | 'tomorrow', language: string, data: DailyHoroscopeData) => void;
    setLoading: (loading: boolean) => void;
    setError: (error: string | null) => void;
    setProfileMissing: (missing: boolean) => void;
    clearCache: () => void;

    // Helpers
    getCachedHoroscope: (date: 'yesterday' | 'today' | 'tomorrow', language: string) => DailyHoroscopeData | null;
    shouldRefetch: (date: 'yesterday' | 'today' | 'tomorrow', language: string) => boolean;
}

// Cache validity duration: 10 minutes
const CACHE_DURATION = 10 * 60 * 1000;

export const useInsightsStore = create<InsightsState>((set, get) => ({
    horoscopes: {},
    isLoading: false,
    error: null,
    isProfileMissing: false,

    setHoroscope: (date, language, data) => set((state) => ({
        horoscopes: {
            ...state.horoscopes,
            [`${date}_${language.toLowerCase()}`]: {
                data,
                fetchedAt: Date.now(),
                language: language.toLowerCase(),
                date,
            },
        },
        error: null,
        isProfileMissing: false,
    })),

    setLoading: (isLoading) => set({ isLoading }),

    setError: (error) => set({ error }),

    setProfileMissing: (isProfileMissing) => set({ isProfileMissing }),

    clearCache: () => set({
        horoscopes: {},
        error: null,
        isProfileMissing: false,
    }),

    getCachedHoroscope: (date, language) => {
        const { horoscopes } = get();
        const key = `${date}_${language.toLowerCase()}`;
        const cached = horoscopes[key];
        if (!cached) return null;

        // Check if cache is still valid
        if (Date.now() - cached.fetchedAt > CACHE_DURATION) {
            return null;
        }
        return cached.data;
    },

    shouldRefetch: (date, language) => {
        const { isLoading } = get();
        if (isLoading) return false;

        const cached = get().getCachedHoroscope(date, language);
        return !cached;
    },
}));
